import { supabase } from './supabase';
import type { CrmLead } from '@/types/crm-lead';
import { pipelineService } from './pipelineService';

export const leadService = {
  async getLeadsByWorkspace(workspaceId: string): Promise<CrmLead[]> {
    const { data, error } = await supabase
      .from('crm_leads')
      .select(`
        *,
        company:company_id (id, name, logo_url)
      `)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return (data || []).map(lead => ({
      ...lead,
      assignee: lead.assignee ? {
        id: lead.assignee.id,
        name: `${lead.assignee.first_name || ''} ${lead.assignee.last_name || ''}`.trim() || lead.assignee.email,
        email: lead.assignee.email,
        avatar: lead.assignee.avatar_url
      } : undefined
    }));
  },

  async getLeadById(leadId: string): Promise<CrmLead | null> {
    const { data, error } = await supabase
      .from('crm_leads')
      .select(`
        *,
        company:company_id (id, name, logo_url)
      `)
      .eq('id', leadId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      ...data,
      assignee: data.assignee ? {
        id: data.assignee.id,
        name: `${data.assignee.first_name || ''} ${data.assignee.last_name || ''}`.trim() || data.assignee.email,
        email: data.assignee.email,
        avatar: data.assignee.avatar_url
      } : undefined
    };
  },

  async createLead(workspaceId: string, userId: string, data: Partial<CrmLead>): Promise<CrmLead> {
    const { data: lead, error } = await supabase
      .from('crm_leads')
      .insert({
        workspace_id: workspaceId,
        created_by: userId,
        ...data
      })
      .select()
      .single();

    if (error) throw error;
    return lead;
  },

  async updateLead(leadId: string, updates: Partial<CrmLead>): Promise<CrmLead> {
    const { data: lead, error } = await supabase
      .from('crm_leads')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', leadId)
      .select()
      .single();

    if (error) throw error;
    
    // Invalidate pipeline caches since deal contact information may have changed
    pipelineService.clearCache();
    
    return lead;
  },

  async deleteLead(leadId: string): Promise<void> {
    const { error } = await supabase
      .from('crm_leads')
      .delete()
      .eq('id', leadId);

    if (error) throw error;
  },

  async convertLeadToClient(leadId: string, workspaceId: string): Promise<void> {
    // 1. Fetch the lead
    const lead = await this.getLeadById(leadId);
    if (!lead || lead.status === 'converted') return;

    // 2. Create workspace invitation
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    
    // Check if user already exists
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', lead.email || '')
      .maybeSingle();
      
    let convertedClientId = null;

    if (profile) {
      // User exists, add to workspace_members directly
      const { data: existingMember } = await supabase
        .from('workspace_members')
        .select('id')
        .eq('workspace_id', workspaceId)
        .eq('user_id', profile.id)
        .maybeSingle();

      const memberData: any = {
        workspace_id: workspaceId,
        user_id: profile.id,
        role: 'client' as const,
        status: 'active' as const,
        website: lead.website,
        company_name: lead.company_name,
        country: lead.address?.country,
        state: lead.address?.state,
        city: lead.address?.city,
        zip: lead.address?.zip,
        street_address: lead.address?.streetAddress
      };

      if (lead.company_id) {
        memberData.company_id = lead.company_id;
      }

      if (!existingMember) {
        await supabase.from('workspace_members').insert(memberData);
      } else {
        await supabase.from('workspace_members').update(memberData).eq('id', existingMember.id);
      }
      
      convertedClientId = profile.id;
      
    } else if (lead.email) {
      // Create invitation
      const invData: any = {
        workspace_id: workspaceId,
        email: lead.email,
        role: 'client',
        invited_by: currentUser?.id,
        first_name: lead.first_name,
        last_name: lead.last_name,
        phone: lead.phone,
        website: lead.website,
        company_name: lead.company_name,
        country: lead.address?.country,
        state: lead.address?.state,
        city: lead.address?.city,
        zip: lead.address?.zip,
        street_address: lead.address?.streetAddress,
      };
      
      if (lead.company_id) {
        invData.company_id = lead.company_id;
      }

      const { data: invitation, error: invError } = await supabase
        .from('workspace_invitations')
        .upsert(invData, { onConflict: 'workspace_id,email' })
        .select('id')
        .single();
        
      if (!invError && invitation) {
        convertedClientId = invitation.id;
      }
    }

    // 3. Link to company if applicable
    if (convertedClientId && lead.company_id) {
      // Check if it's the first client
      const { count } = await supabase
        .from('workspace_company_clients')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', lead.company_id);

      const isOwner = count === 0;

      await supabase
        .from('workspace_company_clients')
        .upsert({
          company_id: lead.company_id,
          user_id: convertedClientId,
          role: isOwner ? 'Owner' : 'Member',
          is_owner: isOwner
        }, { onConflict: 'company_id,user_id' });
    }

    // 4. Update lead status
    await this.updateLead(leadId, {
      status: 'converted',
      converted_client_id: convertedClientId || undefined,
      converted_at: new Date().toISOString()
    });
  },

  async getLeadsForDealPicker(workspaceId: string): Promise<any[]> {
    // Get leads
    const { data: leads } = await supabase
      .from('crm_leads')
      .select('id, first_name, last_name, email, phone, status, source, designation, website, company_name, company_category, company_id')
      .eq('workspace_id', workspaceId)
      .neq('status', 'converted');

    // Get clients (members)
    const { data: members } = await supabase
      .from('workspace_members')
      .select('user_id, first_name, last_name, company_name, category, website, company_id')
      .eq('workspace_id', workspaceId)
      .eq('role', 'client')
      .eq('status', 'active');
      
    let clients: any[] = [];
    if (members && members.length > 0) {
      const userIds = members.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, phone, designation, source')
        .in('id', userIds);
        
      if (profiles) {
        clients = profiles.map(p => {
          const mem = members.find(m => m.user_id === p.id);
          return {
            id: p.id,
            first_name: mem?.first_name || p.first_name || '',
            last_name: mem?.last_name || p.last_name || '',
            email: p.email || '',
            phone: p.phone || '',
            source: p.source || '',
            designation: p.designation || '',
            company_name: mem?.company_name || '',
            company_category: mem?.category || '',
            website: mem?.website || '',
            company_id: mem?.company_id || null,
            type: 'client'
          };
        });
      }
    }

    const formattedLeads = (leads || []).map(l => ({
      ...l,
      type: 'lead'
    }));

    return [...formattedLeads, ...clients];
  },

  async getCompaniesForPicker(workspaceId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('workspace_companies')
      .select('id, name, category, website, phone, email, logo_url')
      .eq('workspace_id', workspaceId)
      .order('name', { ascending: true });

    if (error) {
      console.error('Failed to fetch workspace companies:', error);
      return [];
    }
    return data || [];
  }
};
